/**
 * Fountain Parser
 *
 * A simplified parser for Fountain screenplay syntax.
 * Fountain is a markdown-like syntax for writing screenplays.
 *
 * FOUNTAIN BASICS:
 * ================
 * - Scene headings: Lines starting with INT., EXT., INT/EXT., or I/E.
 * - Character names: ALL CAPS lines (followed by dialogue)
 * - Dialogue: Lines following a character name
 * - Parentheticals: Lines in (parentheses) within dialogue
 * - Transitions: Lines ending with TO: (e.g., "CUT TO:")
 * - Action: Everything else (regular narrative text)
 *
 * Learn more: https://fountain.io/syntax/
 *
 * This is a simplified implementation - a full Fountain parser would handle
 * more features like title pages, notes, sections, and forced elements.
 */

/**
 * Types of screenplay elements we can identify
 */
export type ScreenplayLineType =
  | 'scene-heading'
  | 'character'
  | 'dialogue'
  | 'parenthetical'
  | 'transition'
  | 'action'
  | 'empty';

/**
 * A parsed line of screenplay
 */
export interface ScreenplayLine {
  type: ScreenplayLineType;
  text: string;
  raw: string;
}

/**
 * Regular expressions for identifying Fountain elements
 */
const PATTERNS = {
  // Scene headings start with INT., EXT., INT/EXT., INT./EXT., I/E., or are forced with .
  sceneHeading: /^(INT\.|EXT\.|INT\.?\/EXT\.|I\/E\.|\.)(.+)/i,

  // Transitions end with TO: (CUT TO:, FADE TO:, etc.) or are forced with >
  transition: /^(>|.+\s+TO:)$/i,

  // Character names are ALL CAPS (at least 2 chars), optionally with (V.O.) or (O.S.)
  // Must not be a scene heading or transition
  character: /^[A-Z][A-Z0-9 ]+(\s*\([^)]+\))?$/,

  // Parentheticals are wrapped in parentheses
  parenthetical: /^\s*\([^)]+\)\s*$/,

  // Centered text (for titles, etc.)
  centered: /^>(.+)<$/,
};

/**
 * Parse a block of screenplay text into typed lines
 *
 * @param text - Raw screenplay text
 * @returns Array of parsed screenplay lines
 *
 * @example
 * const lines = parseScreenplay(`
 * INT. COFFEE SHOP - DAY
 *
 * JOHN enters, looking tired.
 *
 * JOHN
 * I need coffee.
 * `);
 */
export function parseScreenplay(text: string): ScreenplayLine[] {
  if (!text || !text.trim()) {
    return [];
  }

  const rawLines = text.split('\n');
  const result: ScreenplayLine[] = [];

  // Track state for context-aware parsing
  let previousType: ScreenplayLineType = 'action';
  let inDialogue = false;

  for (let i = 0; i < rawLines.length; i++) {
    const raw = rawLines[i];
    const trimmed = raw.trim();

    // Skip empty lines but track them for context
    if (!trimmed) {
      inDialogue = false;
      continue;
    }

    const line: ScreenplayLine = {
      type: 'action',
      text: trimmed,
      raw,
    };

    // Check for scene heading
    const sceneMatch = trimmed.match(PATTERNS.sceneHeading);
    if (sceneMatch) {
      line.type = 'scene-heading';
      // Remove the forced scene heading marker if present
      line.text = trimmed.startsWith('.') ? trimmed.slice(1) : trimmed;
      inDialogue = false;
    }
    // Check for transition
    else if (PATTERNS.transition.test(trimmed)) {
      line.type = 'transition';
      // Remove the forced transition marker if present
      line.text = trimmed.startsWith('>') ? trimmed.slice(1).trim() : trimmed;
      inDialogue = false;
    }
    // Check for centered text
    else if (PATTERNS.centered.test(trimmed)) {
      line.type = 'action'; // Treat centered as styled action
      const match = trimmed.match(PATTERNS.centered);
      line.text = match ? match[1].trim() : trimmed;
      inDialogue = false;
    }
    // Check for parenthetical (only valid within dialogue)
    else if (inDialogue && PATTERNS.parenthetical.test(trimmed)) {
      line.type = 'parenthetical';
    }
    // Check for character name
    else if (isCharacterName(trimmed, previousType)) {
      line.type = 'character';
      inDialogue = true;
    }
    // If we're in dialogue mode and it's not a special element, it's dialogue
    else if (inDialogue) {
      line.type = 'dialogue';
    }
    // Otherwise it's action
    else {
      line.type = 'action';
      inDialogue = false;
    }

    result.push(line);
    previousType = line.type;
  }

  return result;
}

/**
 * Check if a line is a character name
 *
 * Character names must:
 * - Be ALL CAPS (with optional parenthetical extensions like (V.O.))
 * - Not be a scene heading or transition
 * - Be preceded by a blank line or be at the start
 */
function isCharacterName(text: string, previousType: ScreenplayLineType): boolean {
  // Must match character pattern
  if (!PATTERNS.character.test(text)) {
    return false;
  }

  // Must not be a scene heading keyword
  if (PATTERNS.sceneHeading.test(text)) {
    return false;
  }

  // Must not be a transition
  if (PATTERNS.transition.test(text)) {
    return false;
  }

  // Common words that shouldn't be character names
  const nonCharacterWords = ['THE', 'A', 'AN', 'AND', 'BUT', 'OR', 'SO', 'FOR'];
  if (nonCharacterWords.includes(text)) {
    return false;
  }

  return true;
}

/**
 * Format screenplay lines back to plain text
 *
 * @param lines - Parsed screenplay lines
 * @returns Formatted plain text
 */
export function formatScreenplay(lines: ScreenplayLine[]): string {
  return lines.map((line) => line.text).join('\n');
}

/**
 * Convert plain text to basic Fountain format
 *
 * This is a simple helper that adds some basic formatting hints.
 * It's not a full conversion - just helps structure plain text.
 *
 * @param text - Plain text to convert
 * @returns Text with Fountain hints added
 */
export function textToFountain(text: string): string {
  // This is a very basic helper - in practice, users should
  // write in Fountain format directly or use a full converter

  const lines = text.split('\n');
  const result: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // If it looks like a location description, format as scene heading
    if (/^(inside|outside|interior|exterior)\b/i.test(trimmed)) {
      const prefix = /^(inside|interior)/i.test(trimmed) ? 'INT.' : 'EXT.';
      const location = trimmed.replace(/^(inside|outside|interior|exterior)\s+/i, '');
      result.push(`${prefix} ${location.toUpperCase()}`);
    }
    // Otherwise, keep as-is
    else {
      result.push(line);
    }
  }

  return result.join('\n');
}
